import moment from 'moment';
import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactHtmlParser from 'react-html-parser';
import Cookies from 'universal-cookie';

import IconClose from '@components/Images/Rebranding/IconClose';
import useEventTracking from '@hooks/useEventTracking';
import {
  ClickTarget,
  EventProperties,
  ImpressionElement,
  TrackingLocation,
  click,
  impression,
} from '@tracking';

import Wrapper from './Wrapper';

interface AwarenessProps {
  analytics?: EventProperties;
  className?: string;
  headline: string;
}

const cookies = new Cookies();
const AWARENESS_CLOSED_COOKIE = 'sticky-banner-top-closed-awareness';
const AWARENESS_COOKIE = 'sticky-banner-top-show-awareness';

const Awareness = ({ analytics, className = '', headline }: AwarenessProps): JSX.Element => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const isFirstView = useRef<boolean>(true);
  const { Track, trackEvent } = useEventTracking({
    ...analytics,
    location: TrackingLocation.AwarenessBar,
  });

  useEffect(() => {
    const expiryDate = moment().add(1, 'days');
    const existingValue = cookies.get(AWARENESS_COOKIE);
    cookies.set(AWARENESS_COOKIE, existingValue ? parseInt(existingValue) + 1 : 1, {
      expires: expiryDate.toDate(),
    });
  }, []);

  const canShowBanner = useCallback(() => {
    const isBannerClosed = cookies.get(AWARENESS_CLOSED_COOKIE);
    const canView = !isBannerClosed && isVisible;

    if (canView && isFirstView.current) {
      isFirstView.current = false;
      trackEvent(impression({ impressionElement: ImpressionElement.AwarenessBar }));
    }
    return canView;
  }, [isVisible, trackEvent]);

  const closeBanner = () => {
    setIsVisible(false);
    cookies.set(AWARENESS_CLOSED_COOKIE, true, { path: '/' });
    trackEvent(click({ clickTarget: ClickTarget.Close }));
  };

  const trackCTAClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if ('A' === target.nodeName) {
      trackEvent(
        click({
          clickLink: (target as HTMLAnchorElement).href,
          clickTarget: ClickTarget.AnchorLink,
          clickText: target.innerText,
        })
      );
    }
  };

  return (
    <Track>
      {canShowBanner() && (
        <Wrapper
          className={`relative bg-primary-magenta !px-24 !py-4 sm:!px-16 md:!py-8 ${className}`}
        >
          <div className="awareness-bar flex flex-row flex-wrap items-center justify-center">
            <div
              className="mt-0 pb-0 pr-0 text-center font-suisse-intl text-description-sm font-medium text-white *:flex *:flex-col sm:mt-0 sm:pb-0 sm:*:block sm:*:flex-none md:mt-8 md:pb-8 md:pr-[9%] md:text-body-lg"
              onClickCapture={trackCTAClick}
            >
              {ReactHtmlParser(headline)}
            </div>
            <IconClose
              onClick={closeBanner}
              className="absolute right-0 top-0 m-8 w-12 cursor-pointer stroke-2 text-white md:bottom-0 md:left-[82%] md:m-auto md:w-[26px] md:stroke-1 lg:left-[70%]"
              data-testid="awareness-bar-close"
              role="button"
              aria-pressed="false"
            />
          </div>
        </Wrapper>
      )}
    </Track>
  );
};

export default Awareness;
